import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Card, Spinner } from '@agion/ui'
import { DollarSign, Cpu, HardDrive, Wifi, TrendingUp, TrendingDown } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { getSummary, getBreakdown } from './infra_cost.service'

interface CostSummary {
  totalMonthly: number
  compute: number
  storage: number
  network: number
}

interface EnvironmentCost {
  environment: string
  monthlyCost: number
  trend: 'up' | 'down' | 'stable'
  trendPercent: number
}

interface ProviderCost {
  provider: string
  monthlyCost: number
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function CostsPage() {
  const { t } = useTranslation(['pages'])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<CostSummary | null>(null)
  const [envCosts, setEnvCosts] = useState<EnvironmentCost[]>([])
  const [providerCosts, setProviderCosts] = useState<ProviderCost[]>([])

  const loadCosts = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const [summaryData, breakdownData] = await Promise.all([
        getSummary(),
        getBreakdown(),
      ])
      const computeItem = breakdownData.items.find(i => i.category === 'compute')
      const storageItem = breakdownData.items.find(i => i.category === 'storage')
      const networkItem = breakdownData.items.find(i => i.category === 'network')
      setSummary({
        totalMonthly: summaryData.totalCost,
        compute: computeItem?.cost ?? 0,
        storage: storageItem?.cost ?? 0,
        network: networkItem?.cost ?? 0,
      })
      setEnvCosts([])
      setProviderCosts([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cost data')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadCosts()
  }, [loadCosts])

  const summaryCards = summary
    ? [
        {
          label: t('pages:costs.summary.total', 'Total Monthly'),
          value: formatCurrency(summary.totalMonthly),
          icon: DollarSign,
          color: 'bg-blue-500',
          textColor: 'text-blue-600',
        },
        {
          label: t('pages:costs.summary.compute', 'Compute'),
          value: formatCurrency(summary.compute),
          icon: Cpu,
          color: 'bg-violet-500',
          textColor: 'text-violet-600',
        },
        {
          label: t('pages:costs.summary.storage', 'Storage'),
          value: formatCurrency(summary.storage),
          icon: HardDrive,
          color: 'bg-amber-500',
          textColor: 'text-amber-600',
        },
        {
          label: t('pages:costs.summary.network', 'Network'),
          value: formatCurrency(summary.network),
          icon: Wifi,
          color: 'bg-emerald-500',
          textColor: 'text-emerald-600',
        },
      ]
    : []

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        icon={DollarSign}
        title={t('pages:costs.title', 'Costs')}
        description={t('pages:costs.description', 'Overview of infrastructure costs and breakdown')}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Card>
          <div className="p-8 text-center text-red-600">{error}</div>
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {summaryCards.map((card) => (
              <Card key={card.label}>
                <div className="p-4 flex items-center gap-4">
                  <div className={`w-12 h-12 ${card.color} rounded-xl flex items-center justify-center`}>
                    <card.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${card.textColor}`}>{card.value}</p>
                    <p className="text-xs text-slate-500">{card.label}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Cost by Environment */}
          <Card>
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {t('pages:costs.byEnvironment', 'Cost by Environment')}
              </h2>
            </div>
            {envCosts.length === 0 ? (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                {t('pages:costs.noEnvironments', 'No environment cost data available.')}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <span>{t('pages:costs.columns.environment', 'Environment')}</span>
                  <span>{t('pages:costs.columns.monthlyCost', 'Monthly Cost')}</span>
                  <span>{t('pages:costs.columns.trend', 'Trend')}</span>
                </div>
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {envCosts.map((env) => (
                    <div
                      key={env.environment}
                      className="grid grid-cols-3 gap-4 px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <span className="text-slate-900 dark:text-slate-100">{env.environment}</span>
                      <span className="text-slate-700 dark:text-slate-300 font-mono">
                        {formatCurrency(env.monthlyCost)}
                      </span>
                      <span>
                        <Badge variant={env.trend === 'up' ? 'error' : env.trend === 'down' ? 'success' : 'default'}>
                          {env.trend === 'up' ? (
                            <TrendingUp className="h-3 w-3 mr-1 inline" />
                          ) : env.trend === 'down' ? (
                            <TrendingDown className="h-3 w-3 mr-1 inline" />
                          ) : null}
                          {env.trendPercent}%
                        </Badge>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>

          {/* Cost by Provider */}
          <Card>
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {t('pages:costs.byProvider', 'Cost by Provider')}
              </h2>
            </div>
            {providerCosts.length === 0 ? (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                {t('pages:costs.noProviders', 'No provider cost data available.')}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <span>{t('pages:costs.columns.provider', 'Provider')}</span>
                  <span>{t('pages:costs.columns.monthlyCost', 'Monthly Cost')}</span>
                </div>
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  {providerCosts.map((provider) => (
                    <div
                      key={provider.provider}
                      className="grid grid-cols-2 gap-4 px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <span className="text-slate-900 dark:text-slate-100">{provider.provider}</span>
                      <span className="text-slate-700 dark:text-slate-300 font-mono">
                        {formatCurrency(provider.monthlyCost)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
